import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Code2, Smartphone, Palette, Compass, Database, ArrowRight,
  Sparkles, ShoppingCart, Cloud, Plug, Wrench, Layers, Search,
  CreditCard, Users,
} from "lucide-react";
import { PageShell, PageHero } from "@/components/site/page-shell";
import { CTABanner } from "@/components/sections/cta-banner";
import { TopRelatedDigitalItems } from "@/components/sections/top-related-digital-items";
import { Input } from "@/components/ui/input";

export const Route = createFileRoute("/services/")({
  component: ServicesPage,
  head: () => ({
    meta: [
      { title: "Services — CoderLity" },
      { name: "description", content: "Explore every CoderLity service: web, mobile, UI/UX, ERP, SaaS, AI, DevOps, e-commerce and more — with a dedicated deep-dive page for each specialty." },
      { property: "og:title", content: "Services — CoderLity" },
      { property: "og:description", content: "End-to-end digital services with dedicated pages for every specialty." },
      { property: "og:url", content: "/services" },
    ],
    links: [{ rel: "canonical", href: "https://coderlity.com/services" }],
  }),
});

type Child = { slug: string; title: string };
type Category = {
  slug: string;
  icon: typeof Code2;
  title: string;
  tagline: string;
  groups: { label: string; items: Child[] }[];
};

const CATEGORIES: Category[] = [
  {
    slug: "web-development",
    icon: Code2,
    title: "Web Development",
    tagline: "SaaS, portals & marketing sites built to scale.",
    groups: [
      { label: "Stacks", items: [
        { slug: "react-nextjs", title: "React & Next.js" },
        { slug: "mern-stack", title: "MERN Stack" },
        { slug: "laravel-php", title: "Laravel / PHP" },
        { slug: "wordpress", title: "WordPress" },
      ]},
      { label: "Solutions", items: [
        { slug: "saas-platforms", title: "SaaS Platforms" },
        { slug: "marketing-sites", title: "Marketing Sites" },
        { slug: "web-portals", title: "Web Portals" },
        { slug: "progressive-web-apps", title: "Progressive Web Apps" },
      ]},
      { label: "Services", items: [
        { slug: "api-integration", title: "API Integration" },
        { slug: "performance-audits", title: "Performance Audits" },
        { slug: "maintenance-support", title: "Maintenance & Support" },
      ]},
    ],
  },
  {
    slug: "mobile-application",
    icon: Smartphone,
    title: "Mobile Application",
    tagline: "Native and cross-platform iOS & Android apps.",
    groups: [
      { label: "Platforms", items: [
        { slug: "ios-swift", title: "iOS (Swift)" },
        { slug: "android-kotlin", title: "Android (Kotlin)" },
        { slug: "react-native", title: "React Native" },
        { slug: "flutter", title: "Flutter" },
      ]},
      { label: "App Types", items: [
        { slug: "on-demand-apps", title: "On-Demand Apps" },
        { slug: "marketplace-apps", title: "Marketplace Apps" },
        { slug: "fintech-apps", title: "Fintech Apps" },
        { slug: "social-apps", title: "Social Apps" },
      ]},
      { label: "Services", items: [
        { slug: "app-strategy", title: "App Strategy" },
        { slug: "uiux-prototyping", title: "UI/UX Prototyping" },
        { slug: "publishing-aso", title: "Publishing & ASO" },
      ]},
    ],
  },
  {
    slug: "ui-ux-design",
    icon: Palette,
    title: "UI / UX Design",
    tagline: "Research-driven UX and design systems your team will love.",
    groups: [
      { label: "Design", items: [
        { slug: "product-design", title: "Product Design" },
        { slug: "web-design", title: "Web Design" },
        { slug: "mobile-design", title: "Mobile Design" },
        { slug: "design-systems", title: "Design Systems" },
      ]},
      { label: "Research", items: [
        { slug: "user-research", title: "User Research" },
        { slug: "usability-testing", title: "Usability Testing" },
        { slug: "wireframing", title: "Wireframing" },
      ]},
      { label: "Branding", items: [
        { slug: "brand-identity", title: "Brand Identity" },
        { slug: "logo-visual", title: "Logo & Visual" },
        { slug: "motion-graphics", title: "Motion Graphics" },
      ]},
    ],
  },
  {
    slug: "tech-consultation",
    icon: Compass,
    title: "Tech Consultation",
    tagline: "Strategy, architecture and compliance guidance.",
    groups: [
      { label: "Strategy", items: [
        { slug: "product-discovery", title: "Product Discovery" },
        { slug: "mvp-planning", title: "MVP Planning" },
        { slug: "tech-stack-audit", title: "Tech Stack Audit" },
      ]},
      { label: "Architecture", items: [
        { slug: "cloud-architecture", title: "Cloud Architecture" },
        { slug: "microservices", title: "Microservices" },
        { slug: "devops-cicd", title: "DevOps & CI/CD" },
      ]},
      { label: "Security", items: [
        { slug: "security-audit", title: "Security Audit" },
        { slug: "compliance-gdpr", title: "Compliance & GDPR" },
      ]},
    ],
  },
  {
    slug: "erp-solution",
    icon: Database,
    title: "ERP Solution",
    tagline: "Modular ERPs for your operations, top to bottom.",
    groups: [
      { label: "Modules", items: [
        { slug: "inventory-warehouse", title: "Inventory & Warehouse" },
        { slug: "sales-purchase", title: "Sales & Purchase" },
        { slug: "manufacturing", title: "Manufacturing" },
        { slug: "supply-chain", title: "Supply Chain" },
        { slug: "accounting-invoicing", title: "Accounting & Invoicing" },
        { slug: "reporting-analytics", title: "Reporting & Analytics" },
        { slug: "payroll", title: "Payroll" },
      ]},
      { label: "Platform", items: [
        { slug: "workflow-automation", title: "Workflow Automation" },
        { slug: "role-based-permissions", title: "Role-Based Permissions" },
        { slug: "data-migration-setup", title: "Data Migration & Setup" },
        { slug: "training-support", title: "Training & Support" },
      ]},
    ],
  },
  {
    slug: "pos-software",
    icon: CreditCard,
    title: "POS Software",
    tagline: "Retail & restaurant point of sale.",
    groups: [
      { label: "Retail POS", items: [
        { slug: "single-store-pos", title: "Single-store POS" },
        { slug: "multi-branch-pos", title: "Multi-branch POS" },
        { slug: "barcode-inventory", title: "Barcode & Inventory" },
      ]},
      { label: "Restaurant POS", items: [
        { slug: "table-management", title: "Table Management" },
        { slug: "kitchen-display", title: "Kitchen Display" },
        { slug: "online-orders", title: "Online Orders" },
      ]},
    ],
  },
  {
    slug: "hrm-payroll",
    icon: Users,
    title: "HRM & Payroll",
    tagline: "People operations, hiring to payroll.",
    groups: [
      { label: "HR", items: [
        { slug: "employee-records", title: "Employee Records" },
        { slug: "attendance-leave", title: "Attendance & Leave" },
        { slug: "recruitment", title: "Recruitment" },
      ]},
      { label: "Payroll", items: [
        { slug: "payroll-processing", title: "Payroll Processing" },
        { slug: "tax-compliance", title: "Tax Compliance" },
      ]},
    ],
  },
  {
    slug: "industry-solutions",
    icon: Layers,
    title: "Industry Solutions",
    tagline: "Pre-built systems for vertical industries.",
    groups: [
      { label: "Education", items: [
        { slug: "school-management", title: "School Management" },
        { slug: "lms-platform", title: "LMS Platform" },
      ]},
      { label: "Healthcare", items: [
        { slug: "hospital-management", title: "Hospital Management" },
        { slug: "clinic-pharmacy", title: "Clinic & Pharmacy" },
      ]},
      { label: "Logistics", items: [
        { slug: "fleet-management", title: "Fleet Management" },
        { slug: "warehouse-management", title: "Warehouse Management" },
      ]},
    ],
  },
];

const MORE_SERVICES: { slug: string; icon: typeof Code2; title: string; desc: string }[] = [
  { slug: "saas-development", icon: Layers, title: "SaaS Development", desc: "Multi-tenant SaaS built to scale." },
  { slug: "ai-automation", icon: Sparkles, title: "AI Automation", desc: "LLM workflows, agents & automations." },
  { slug: "ecommerce", icon: ShoppingCart, title: "E-commerce", desc: "Shopify, Woo & headless commerce." },
  { slug: "devops", icon: Cloud, title: "DevOps", desc: "Cloud infra, CI/CD & observability." },
  { slug: "api-integration", icon: Plug, title: "API Integration", desc: "Stripe, ERP, CRM & bespoke APIs." },
  { slug: "maintenance", icon: Wrench, title: "Maintenance", desc: "Ongoing care with SLAs & reports." },
];

function ServicesPage() {
  const [activeSlug, setActiveSlug] = useState<string>(CATEGORIES[0].slug);
  const [query, setQuery] = useState("");

  const active = CATEGORIES.find((c) => c.slug === activeSlug) ?? CATEGORIES[0];

  // Search across all categories + sub-services + more services
  const searchResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    const results: { to: string; title: string; parent: string }[] = [];
    for (const c of CATEGORIES) {
      if (c.title.toLowerCase().includes(q)) {
        results.push({ to: `/services/${c.slug}`, title: c.title, parent: "Category" });
      }
      for (const g of c.groups) {
        for (const it of g.items) {
          if (it.title.toLowerCase().includes(q)) {
            results.push({ to: `/services/${c.slug}/${it.slug}`, title: it.title, parent: c.title });
          }
        }
      }
    }
    for (const s of MORE_SERVICES) {
      if (s.title.toLowerCase().includes(q) || s.desc.toLowerCase().includes(q)) {
        results.push({ to: `/services/${s.slug}`, title: s.title, parent: "Specialized" });
      }
    }
    return results.slice(0, 20);
  }, [query]);

  return (
    <PageShell>
      <PageHero
        eyebrow="Services"
        title="Everything you need, expertly delivered"
        sub="Pick a practice area — or search 60+ specialties across engineering, design, AI and cloud."
      />

      {/* Search bar */}
      <section className="pt-2">
        <div className="mx-auto max-w-3xl px-4 lg:px-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search services (e.g. React, Payroll, DevOps)…"
              className="h-12 rounded-full pl-11 pr-4 text-sm"
              aria-label="Search services"
            />
          </div>
          {searchResults && (
            <div className="mt-3 rounded-2xl border border-border bg-card p-2 shadow-card">
              {searchResults.length === 0 ? (
                <div className="px-3 py-4 text-sm text-muted-foreground">No matches. Try another keyword.</div>
              ) : (
                <ul className="max-h-80 overflow-auto">
                  {searchResults.map((r) => (
                    <li key={r.to}>
                      <Link
                        to={r.to as never}
                        className="group flex items-center justify-between rounded-lg px-3 py-2 hover:bg-secondary transition"
                      >
                        <span className="min-w-0 truncate text-sm font-medium">{r.title}</span>
                        <span className="ml-3 shrink-0 text-[11px] uppercase tracking-wider text-muted-foreground">{r.parent}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Interactive explorer: tabs + detail */}
      <section className="py-12">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          {/* Category tabs — horizontal scroll on mobile */}
          <div
            className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1"
            role="tablist"
            aria-label="Service categories"
          >
            {CATEGORIES.map((c) => {
              const isActive = c.slug === activeSlug;
              const Icon = c.icon;
              return (
                <button
                  key={c.slug}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveSlug(c.slug)}
                  className={
                    "group shrink-0 inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition " +
                    (isActive
                      ? "border-primary/40 bg-primary/10 text-primary shadow-sm"
                      : "border-border bg-card text-muted-foreground hover:border-primary/30 hover:text-foreground")
                  }
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="truncate">{c.title}</span>
                </button>
              );
            })}
          </div>

          {/* Active category panel */}
          <AnimatePresence mode="wait">
            <motion.div
              key={active.slug}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className="mt-6 rounded-3xl border border-border bg-card overflow-hidden"
            >
              {/* Header row */}
              <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-4 border-b border-border/70 bg-muted/30 p-5 sm:p-6 sm:flex sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-start gap-4">
                  <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
                    <active.icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="truncate text-xl sm:text-2xl font-bold tracking-tight">{active.title}</h2>
                    <p className="mt-0.5 text-sm text-muted-foreground line-clamp-2">{active.tagline}</p>
                  </div>
                </div>
                <Link
                  to={`/services/${active.slug}` as never}
                  className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-gradient-brand px-4 py-2 text-xs font-semibold text-white shadow-glow"
                >
                  Overview <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>

              {/* Sub-services grid */}
              <div className="p-5 sm:p-6 grid gap-6 md:grid-cols-3">
                {active.groups.map((g) => (
                  <div key={g.label}>
                    <h3 className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">{g.label}</h3>
                    <ul className="mt-2 space-y-1.5">
                      {g.items.map((it) => (
                        <li key={it.slug}>
                          <Link
                            to={`/services/${active.slug}/${it.slug}` as never}
                            className="group flex items-center justify-between rounded-lg border border-transparent px-3 py-2 hover:border-border hover:bg-secondary/60 transition"
                          >
                            <span className="min-w-0 truncate text-sm font-medium">{it.title}</span>
                            <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-primary" />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* Specialized standalone services */}
      <section className="pb-14">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="mb-6 flex items-end justify-between gap-4">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold tracking-tight">Specialized practices</h2>
              <p className="mt-1 text-sm text-muted-foreground">Standalone offerings — engage on their own or bundle with any category above.</p>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {MORE_SERVICES.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}` as never}
                className="group flex items-start gap-4 rounded-2xl border border-border bg-card p-5 hover:border-primary/40 transition"
              >
                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
                  <s.icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-semibold">{s.title}</h3>
                  <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{s.desc}</p>
                </div>
                <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-primary" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Custom CTA */}
      <section className="pb-14">
        <div className="mx-auto max-w-5xl px-4 lg:px-8">
          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-6 rounded-3xl border border-border bg-gradient-to-br from-card to-muted/40 p-6 sm:p-8 sm:flex sm:justify-between">
            <div className="min-w-0">
              <h3 className="text-lg sm:text-xl font-bold tracking-tight">Can't find what you need?</h3>
              <p className="mt-1 text-sm text-muted-foreground">Tell us about your project — we'll scope a custom engagement in 24 hours.</p>
            </div>
            <Link
              to="/contact"
              className="shrink-0 inline-flex items-center gap-2 rounded-full bg-gradient-brand px-5 py-2.5 text-sm font-semibold text-white shadow-glow"
            >
              Talk to our team <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <TopRelatedDigitalItems context="services" />

      <CTABanner />
    </PageShell>
  );
}
